/**
 * Scaling tab — pool scaling configuration: foundational clamps, demand
 * sources, and the capacity schedule. All edits go through the shared
 * config draft (usePoolConfigDraft); the route renders the save bar whenever
 * the draft is dirty.
 *
 * Demand sources are presented as one sub-tab per source (Reactive /
 * Scheduled / Predictive / Backfill) plus a Boosts & Dampens sub-tab for the
 * expiring runtime interventions — mirroring the mediation model, where all
 * five produce signals that are mediated together (floors maximized,
 * ceilings minimized). Each source sub-tab carries its own enable/disable
 * toggle and tuning knobs; the scheduled sub-tab owns the capacity-schedule
 * span editor, and the predictive sub-tab embeds the full forecast
 * configuration + observability charts.
 */

import { useState } from 'react';
import { CapacityScheduleEditor } from '../profiles/CapacityScheduleEditor';
import styles from '../../routes/admin/pools/PoolDetail.module.css';
import { PredictiveSourcePanel } from './PredictiveSourcePanel';
import type { BinRow, PoolStats, PredictionRow, QuarantineRow } from './shared';
import type { DemandSourceType, PoolConfigDraftApi, RadiusTier } from './usePoolConfigDraft';

type SourceSubTab = DemandSourceType | 'boosts';

export interface ScalingTabProps {
  draftApi: PoolConfigDraftApi;
  saving: boolean;
  /** Runtime stats — the Boosts & Dampens sub-tab reads active entries. */
  stats: PoolStats | null;
  actionInProgress: string | null;
  onRemoveBoost: (id: number) => void;
  onRemoveDampen: (id: number) => void;
  /** Predictive sub-tab data + actions. */
  predictions: PredictionRow[];
  histogramBins: BinRow[];
  quarantine: QuarantineRow[];
  predictiveTimezoneServed: string;
  onSeedHistory: () => Promise<string>;
}

export function ScalingTab({
  draftApi,
  saving,
  stats,
  actionInProgress,
  onRemoveBoost,
  onRemoveDampen,
  predictions,
  histogramBins,
  quarantine,
  predictiveTimezoneServed,
  onSeedHistory,
}: ScalingTabProps) {
  const { draft, patch, addSource, removeSource, restoreDemandSourceDefaults, existingRadiusKmOverride } = draftApi;
  const [subTab, setSubTab] = useState<SourceSubTab>('reactive');

  if (!draft) return <div className={styles.loading}>Loading configuration…</div>;

  const interventionCount = (stats?.boosts?.length ?? 0) + (stats?.dampens?.length ?? 0);

  const sourceLabel = (source: DemandSourceType, label: string): string => {
    if (!draft.enabledSources.has(source)) return `${label} (off)`;
    if (source === 'predictive' && draft.predictiveShadowMode) return `${label} (shadow)`;
    return label;
  };

  const subTabs: Array<{ key: SourceSubTab; label: string }> = [
    { key: 'reactive', label: sourceLabel('reactive', 'Reactive') },
    { key: 'scheduled', label: sourceLabel('scheduled', 'Scheduled') },
    { key: 'predictive', label: sourceLabel('predictive', 'Predictive') },
    { key: 'backfill', label: sourceLabel('backfill', 'Backfill') },
    { key: 'boosts', label: `Boosts & Dampens${interventionCount > 0 ? ` (${interventionCount})` : ''}` },
  ];

  return (
    <>
      {/* Foundational Scaling Clamps */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Foundational Scaling Clamps</h2>
        </div>
        <div className={styles.formHelp}>
          Hard limits on session capacity. The mediated demand signal is clamped to these bounds regardless of demand source output.
        </div>
        <div className={styles.fieldCluster}>
          <div>
            <label className={styles.formLabel} htmlFor="clamp-min">Min (session slots)</label>
            <input id="clamp-min" className={styles.formInputNarrow} type="number" min="0" value={draft.scalingMin} onChange={e => patch({ scalingMin: e.target.value })} disabled={saving} />
          </div>
          <div>
            <label className={styles.formLabel} htmlFor="clamp-max">Max (session slots)</label>
            <input id="clamp-max" className={styles.formInputNarrow} type="number" min="0" value={draft.scalingMax} onChange={e => patch({ scalingMax: e.target.value })} disabled={saving} />
          </div>
        </div>
      </div>

      {/* Soft Floor */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Soft Floor</h2>
        </div>
        <div className={styles.formHelp}>
          Lets a fully idle pool dip below the min clamp and scheduled floors to release
          standing capacity. Engages only after continuous full idleness; releases instantly
          when a session starts or demand arrives. Predictive floors are never dipped. The
          trade: the first arrival after a dip pays one cold start — enable on pools with
          irregular lulls, leave off for latency-guarantee pools.
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', cursor: saving ? 'default' : 'pointer', fontSize: 'var(--text-sm)', fontWeight: 'var(--weight-semibold)' }}>
          <input
            type="checkbox"
            checked={draft.softFloorEnabled}
            onChange={e => patch({ softFloorEnabled: e.target.checked })}
            disabled={saving}
          />
          Allow dipping below floors during extended idleness
        </label>
        {draft.softFloorEnabled && (
          <div className={styles.fieldCluster} style={{ marginTop: 'var(--space-3)' }}>
            <div>
              <label className={styles.formLabel} htmlFor="sf-after-idle">After continuous idle (seconds)</label>
              <input
                id="sf-after-idle"
                className={styles.formInputNarrow}
                type="number"
                min="60"
                step="60"
                value={draft.softFloorAfterIdleSeconds}
                onChange={e => patch({ softFloorAfterIdleSeconds: e.target.value })}
                disabled={saving}
              />
            </div>
            <div>
              <label className={styles.formLabel} htmlFor="sf-min-slots">Dip floor (session slots)</label>
              <input
                id="sf-min-slots"
                className={styles.formInputNarrow}
                type="number"
                min="0"
                value={draft.softFloorMinSlots}
                onChange={e => patch({ softFloorMinSlots: e.target.value })}
                disabled={saving}
              />
            </div>
          </div>
        )}
      </div>

      {/* Drain Reaper */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Drain Reaper</h2>
        </div>
        <div className={styles.formHelp}>
          Per-instance scale-down: each connected worker carries its own idle clock and is
          terminated once idle past the grace period and connected past boot immunity,
          oldest-idle first, never taking the pool below its mediated floor. Grace and
          immunity are fixed values, not measured — the 720s defaults assume the fleet's
          ~8 min cold start (1.5×); pools with materially different boot times should set
          both to ~1.5× their own cold start.
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', cursor: saving ? 'default' : 'pointer', fontSize: 'var(--text-sm)', fontWeight: 'var(--weight-semibold)' }}>
          <input
            type="checkbox"
            checked={draft.reaperEnabled}
            onChange={e => patch({ reaperEnabled: e.target.checked })}
            disabled={saving}
          />
          Reap idle workers individually (recommended)
        </label>
        {!draft.reaperEnabled && (
          <div className={styles.formHelp} style={{ marginTop: 'var(--space-1)', marginBottom: 0 }}>
            Disabled: scale-down falls back to whole-pool resizes driven by the pool-wide
            idle timer, which any activity anywhere resets.
          </div>
        )}
        {draft.reaperEnabled && (
          <div className={styles.fieldCluster} style={{ marginTop: 'var(--space-3)' }}>
            <div>
              <label className={styles.formLabel} htmlFor="rp-grace">Idle grace (seconds)</label>
              <input
                id="rp-grace"
                className={styles.formInputNarrow}
                type="number"
                min="60"
                step="60"
                value={draft.reaperIdleGraceSeconds}
                onChange={e => patch({ reaperIdleGraceSeconds: e.target.value })}
                disabled={saving}
              />
            </div>
            <div>
              <label className={styles.formLabel} htmlFor="rp-immunity">Boot immunity (seconds)</label>
              <input
                id="rp-immunity"
                className={styles.formInputNarrow}
                type="number"
                min="60"
                step="60"
                value={draft.reaperBootImmunitySeconds}
                onChange={e => patch({ reaperBootImmunitySeconds: e.target.value })}
                disabled={saving}
              />
            </div>
            <div>
              <label className={styles.formLabel} htmlFor="rp-max-reaps">Max reaps per tick</label>
              <input
                id="rp-max-reaps"
                className={styles.formInputNarrow}
                type="number"
                min="1"
                value={draft.reaperMaxReapsPerTick}
                onChange={e => patch({ reaperMaxReapsPerTick: e.target.value })}
                disabled={saving}
              />
            </div>
          </div>
        )}
      </div>

      {/* Demand Sources */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Demand Sources</h2>
          <button
            type="button"
            className={styles.actionButton}
            onClick={restoreDemandSourceDefaults}
            disabled={saving}
            title="Reset demand sources and tuning knobs to current platform defaults. You still need to Save to persist."
          >
            Restore Defaults
          </button>
        </div>
        <div className={styles.formHelp}>
          Each source produces a demand signal (floor &amp; ceiling). Signals are mediated: floors are maximized, ceilings are minimized. Ceiling wins conflicts (cost protection).
        </div>

        {/* Common tuning knobs */}
        <div className={styles.fieldCluster} style={{ marginBottom: 'var(--space-3)' }}>
          <div>
            <label className={styles.formLabel} htmlFor="cooldown-s">Cooldown (seconds, all sources)</label>
            <input id="cooldown-s" className={styles.formInputNarrow} type="number" min="0" value={draft.cooldownSeconds} onChange={e => patch({ cooldownSeconds: e.target.value })} disabled={saving} />
          </div>
        </div>

        {/* Per-source sub-tabs */}
        <div className={styles.tabBar}>
          {subTabs.map(t => (
            <button
              key={t.key}
              type="button"
              className={subTab === t.key ? styles.tabButtonActive : styles.tabButton}
              aria-current={subTab === t.key ? 'true' : undefined}
              onClick={() => setSubTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {subTab === 'reactive' && (
          <div className={styles.sourceCard}>
            <SourceToggleRow
              enabled={draft.enabledSources.has('reactive')}
              saving={saving}
              onEnable={() => addSource('reactive')}
              onDisable={() => removeSource('reactive')}
            />
            <div className={styles.formHelp}>
              Observes marketplace demand. Floor = current demand, ceiling = unlimited.
            </div>
            {draft.enabledSources.has('reactive') && (
              <div className={styles.fieldCluster}>
                <div>
                  <label className={styles.formLabel} htmlFor="ds-threshold">Scale-Up Threshold (jobs)</label>
                  <input id="ds-threshold" className={styles.formInputNarrow} type="number" min="1" value={draft.scaleUpThreshold} onChange={e => patch({ scaleUpThreshold: e.target.value })} disabled={saving} />
                </div>
                <div>
                  <label className={styles.formLabel} htmlFor="ds-downdelay">Scale-Down Delay (seconds)</label>
                  <input id="ds-downdelay" className={styles.formInputNarrow} type="number" min="0" value={draft.scaleDownDelay} onChange={e => patch({ scaleDownDelay: e.target.value })} disabled={saving} />
                </div>
                <div>
                  <label className={styles.formLabel} htmlFor="ds-evalperiods">Eval Periods</label>
                  <input id="ds-evalperiods" className={styles.formInputNarrow} type="number" min="1" value={draft.evaluationPeriods} onChange={e => patch({ evaluationPeriods: e.target.value })} disabled={saving} />
                </div>
                <div>
                  <label className={styles.formLabel} htmlFor="ds-radius">Observation Radius</label>
                  <select
                    id="ds-radius"
                    className={styles.formSelect}
                    style={{ width: 240 }}
                    value={draft.observationRadiusTier}
                    onChange={e => patch({ observationRadiusTier: e.target.value as RadiusTier })}
                    disabled={saving || existingRadiusKmOverride !== null}
                    title={existingRadiusKmOverride !== null
                      ? `Overridden by explicit radiusKm=${existingRadiusKmOverride} in raw settings`
                      : 'Geographic radius for observing marketplace demand. Matches the marketplace latency tier.'}
                  >
                    <option value="edge">Edge — ~25ms RTT (800 km)</option>
                    <option value="regional">Regional — ~60ms RTT (2,400 km)</option>
                    <option value="continental">Continental — ~120ms RTT (8,000 km)</option>
                    <option value="global">Global — ~250ms RTT (20,015 km)</option>
                  </select>
                  {existingRadiusKmOverride !== null && (
                    <div className={styles.formWarn}>
                      Overridden by explicit radiusKm={existingRadiusKmOverride} in raw settings. Clear it there to re-enable the tier selector.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {subTab === 'scheduled' && (
          <div className={styles.sourceCard}>
            <SourceToggleRow
              enabled={draft.enabledSources.has('scheduled')}
              saving={saving}
              onEnable={() => addSource('scheduled')}
              onDisable={() => removeSource('scheduled')}
            />
            <div className={styles.formHelp}>
              Hand-crafted demand from the capacity spans below. Floor and ceiling are additive across overlapping spans.
            </div>
            {draft.enabledSources.has('scheduled') && (
              <div className={styles.fieldCluster}>
                <div>
                  <label className={styles.formLabel} htmlFor="ds-prescale">Pre-Scale (minutes)</label>
                  <input id="ds-prescale" className={styles.formInputNarrow} type="number" min="0" value={draft.preScaleMinutes} onChange={e => patch({ preScaleMinutes: e.target.value })} disabled={saving} />
                </div>
              </div>
            )}
            {/* The capacity schedule IS the scheduled source's data, so it
                lives here rather than as a page-level section. Spans stay
                visible/editable while the source is disabled so operators can
                stage a schedule before enabling. */}
            <div style={{ marginTop: 'var(--space-3)', paddingTop: 'var(--space-3)', borderTop: 'var(--border-width) dashed var(--border)' }}>
              <h4 className={styles.formSectionTitle}>Capacity Schedule</h4>
              {!draft.enabledSources.has('scheduled') && (
                <div className={styles.formWarn} style={{ marginTop: 0, marginBottom: 'var(--space-2)' }}>
                  The scheduled source is disabled — these spans currently contribute no demand.
                </div>
              )}
              <CapacityScheduleEditor
                value={draft.capacitySchedule}
                onChange={cs => patch({ capacitySchedule: cs })}
                disabled={saving}
              />
            </div>
          </div>
        )}

        {subTab === 'predictive' && (
          <div className={styles.sourceCard}>
            <SourceToggleRow
              enabled={draft.enabledSources.has('predictive')}
              saving={saving}
              enableLabel="Enable (shadow mode)"
              onEnable={() => {
                addSource('predictive');
                patch({ predictiveShadowMode: true });
              }}
              onDisable={() => removeSource('predictive')}
            />
            {!draft.enabledSources.has('predictive') ? (
              <div className={styles.formHelp} style={{ marginBottom: 0 }}>
                Forecasts demand from a weekly adaptive histogram of past observations and
                contributes the smoothed quantile at <code>now + lookahead</code> as a floor —
                competing with reactive/scheduled via max(). Cold-starts at floor=0 until
                ≥ 4 weeks of history accrue (or you seed from session history). Enabling
                starts in shadow mode: validate forecasts for a week before letting them
                drive capacity.
              </div>
            ) : (
              <PredictiveSourcePanel
                draftApi={draftApi}
                saving={saving}
                predictions={predictions}
                histogramBins={histogramBins}
                quarantine={quarantine}
                predictiveTimezoneServed={predictiveTimezoneServed}
                onSeedHistory={onSeedHistory}
              />
            )}
          </div>
        )}

        {subTab === 'backfill' && (
          <div className={styles.sourceCard}>
            <SourceToggleRow
              enabled={draft.enabledSources.has('backfill')}
              saving={saving}
              onEnable={() => addSource('backfill')}
              onDisable={() => removeSource('backfill')}
            />
            <div className={styles.formHelp}>
              Raises demand when available session slots drop below a percentage threshold. Ensures headroom for incoming sessions.
            </div>
            {draft.enabledSources.has('backfill') && (
              <>
                <div className={styles.fieldCluster}>
                  <div>
                    <label className={styles.formLabel} htmlFor="bf-threshold">Threshold (%)</label>
                    <input
                      id="bf-threshold"
                      className={styles.formInputNarrow}
                      type="number" min="0" max="100" step="5"
                      value={Math.round(parseFloat(draft.backfillThreshold) * 100)}
                      onChange={e => patch({ backfillThreshold: String((parseInt(e.target.value) || 50) / 100) })}
                      disabled={saving}
                    />
                  </div>
                  <div>
                    <label className={styles.formLabel} htmlFor="bf-mult">Growth Multiplier (×)</label>
                    <input id="bf-mult" className={styles.formInputNarrow} type="number" min="1" max="5" step="0.1" value={draft.backfillMultiplier} onChange={e => patch({ backfillMultiplier: e.target.value })} disabled={saving} />
                  </div>
                </div>
                <div style={{ marginTop: 'var(--space-3)', paddingTop: 'var(--space-3)', borderTop: 'var(--border-width) dashed var(--border)' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', cursor: saving ? 'default' : 'pointer', fontSize: 'var(--text-sm)', fontWeight: 'var(--weight-semibold)' }}>
                    <input type="checkbox" checked={draft.backfillAdaptive} onChange={e => patch({ backfillAdaptive: e.target.checked })} disabled={saving} />
                    Adaptive multiplier
                  </label>
                  <div className={styles.formHelp} style={{ marginTop: 'var(--space-1)', marginBottom: 0 }}>
                    Scales the growth multiplier by observed session arrival velocity. Idle pools collapse to 1.0× (restore threshold only); pools seeing ≥ saturation arrivals/min get the full multiplier.
                  </div>
                  {draft.backfillAdaptive && (
                    <div className={styles.fieldCluster} style={{ marginTop: 'var(--space-2)' }}>
                      <div>
                        <label className={styles.formLabel} htmlFor="bf-window">Window (minutes)</label>
                        <input id="bf-window" className={styles.formInputNarrow} type="number" min="1" max="60" step="1" value={draft.backfillAdaptiveWindow} onChange={e => patch({ backfillAdaptiveWindow: e.target.value })} disabled={saving} />
                      </div>
                      <div>
                        <label className={styles.formLabel} htmlFor="bf-saturation">Saturation (arrivals/min)</label>
                        <input id="bf-saturation" className={styles.formInputNarrow} type="number" min="0.1" max="100" step="0.1" value={draft.backfillAdaptiveSaturation} onChange={e => patch({ backfillAdaptiveSaturation: e.target.value })} disabled={saving} />
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {subTab === 'boosts' && (
          <div className={styles.sourceCard}>
            <div className={styles.formHelp}>
              Expiring manual interventions layered on top of the mediated signal: boosts add
              slots, dampens cap them. Issue new ones from the <strong>Overview</strong> tab;
              active entries can be cancelled here.
            </div>
            <InterventionList
              title="Active Boosts"
              emptyText="No active boosts."
              entries={(stats?.boosts ?? []).map(b => ({
                id: b.id,
                summary: `+${b.addedSlots} slots`,
                reason: b.reason,
                createdBy: b.createdBy,
                expiresAt: b.expiresAt,
                cancelKey: `remove-boost-${b.id}`,
              }))}
              actionInProgress={actionInProgress}
              onCancel={onRemoveBoost}
            />
            <InterventionList
              title="Active Dampens"
              emptyText="No active dampens."
              entries={(stats?.dampens ?? []).map(d => ({
                id: d.id,
                summary: `−${d.removedSlots} slots`,
                reason: d.reason,
                createdBy: d.createdBy,
                expiresAt: d.expiresAt,
                cancelKey: `remove-dampen-${d.id}`,
              }))}
              actionInProgress={actionInProgress}
              onCancel={onRemoveDampen}
            />
          </div>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Sub-tab building blocks
// ---------------------------------------------------------------------------

function SourceToggleRow({
  enabled,
  saving,
  onEnable,
  onDisable,
  enableLabel = 'Enable',
}: {
  enabled: boolean;
  saving: boolean;
  onEnable: () => void;
  onDisable: () => void;
  enableLabel?: string;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-2)' }}>
      <span className={enabled ? styles.badgeActive : styles.badgeInactive}>
        {enabled ? 'Enabled' : 'Disabled'}
      </span>
      <button
        type="button"
        className={styles.actionButton}
        onClick={enabled ? onDisable : onEnable}
        disabled={saving}
      >
        {enabled ? 'Disable' : enableLabel}
      </button>
    </div>
  );
}

interface InterventionEntry {
  id: number;
  summary: string;
  reason: string;
  createdBy: string;
  expiresAt: number;
  cancelKey: string;
}

function InterventionList({
  title,
  emptyText,
  entries,
  actionInProgress,
  onCancel,
}: {
  title: string;
  emptyText: string;
  entries: InterventionEntry[];
  actionInProgress: string | null;
  onCancel: (id: number) => void;
}) {
  return (
    <div style={{ marginTop: 'var(--space-3)' }}>
      <div style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--weight-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-wide)', color: 'var(--text-secondary)', marginBottom: 'var(--space-2)' }}>
        {title}
      </div>
      {entries.length === 0 ? (
        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>{emptyText}</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
          {entries.map(e => {
            const minsLeft = Math.max(0, Math.ceil((e.expiresAt - Date.now()) / 60000));
            return (
              <div key={e.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 'var(--text-xs)', padding: 'var(--space-1) var(--space-2)', background: 'var(--bg-secondary)' }}>
                <span>
                  <strong>{e.summary}</strong> — {e.reason} <span style={{ opacity: 0.6 }}>by {e.createdBy}, {minsLeft}m left</span>
                </span>
                <button
                  className={styles.actionButton}
                  onClick={() => onCancel(e.id)}
                  disabled={actionInProgress === e.cancelKey}
                >
                  {actionInProgress === e.cancelKey ? '...' : 'Cancel'}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
